<script>
import useVuelidate from '@vuelidate/core';
import {required, maxLength} from '@vuelidate/validators';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';
import AkDialogHeadless from '@components/general/AkDialogHeadless.vue';
import AkFormSubmitted from '@components/general/AkFormSubmitted.vue';
import InternalNote from '@views/threads/parts/InternalNote.vue';
import threadService from '@services/threadService';
import store from '@store';
import MessageMixin from '@mixins/messageMixin';
import AkInputTextArea from '@components/v2/input/AkInputTextArea.vue';
import AkButtonValidationV2 from '@components/v2/general/AkButtonValidationV2.vue';

export default {
  name: 'ThreadMessagesHeader',
  mixins: [MessageMixin],
  components: {
    AkButtonValidationV2,
    AkInputTextArea,
    AkFormSubmitted,
    InternalNote,
    AkDialogHeadless,
    AkButtonActionV2,
  },
  setup() {
    return {v$: useVuelidate()};
  },
  props: {
    conversation: {
      type: Object,
      required: true,
    },
    threadId: {
      type: Number,
      required: true,
    },
    archivable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['archive'],
  data() {
    return {
      newNoteContent: '',
      textareaVisible: false,
      validated: false,
    };
  },
  validations() {
    return {
      newNoteContent: {required, maxLengthValue: maxLength(300)},
    };
  },
  computed: {
    previewUsers() {
      return this.conversation.guests.map((g) => ({...g, display: g.name[0]})) || [];
    },
    clientUsersConcat() {
      return this.conversation.guests.map((g) => g.name).join(', ');
    },
    internalNotes() {
      return this.conversation.internalNotes;
    },
    hasUnreadInternalNotes() {
      if (!this.internalNotes.length) return false;
      const userId = store.state.userInformation.userId;
      const lastNote = this.internalNotes[this.internalNotes.length - 1];
      if (lastNote.author.id === userId) return false;

      const employeeId = store.state.userInformation.employeeId;
      const lastRead = this.conversation.lastInternalNotesRead.find((r) => r.employeeId === employeeId);
      if (!lastRead) return true;
      return new Date(lastRead.dateRead) < new Date(lastNote.dateCreated);
    },
  },
  methods: {
    openInternalNotes() {
      this.validated = false;
      this.textareaVisible = false;
      this.$refs.internalNotesDialog.show();
      if (this.hasUnreadInternalNotes) {
        threadService.markInternalNotesRead(this.threadId).then(() => {
          const employeeId = store.state.userInformation.employeeId;
          const lastRead = this.conversation.lastInternalNotesRead.find((r) => r.employeeId === employeeId);
          const dateRead = new Date().toISOString();
          if (lastRead) lastRead.dateRead = dateRead;
          else this.conversation.lastInternalNotesRead.push({employeeId, dateRead});
        });
      }
    },
    sendInternalNote() {
      this.validated = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.sendButton.init();
        return;
      }

      const content = this.newNoteContent.trim();
      threadService
        .createInternalNote(this.threadId, content)
        .then((note) => {
          this.internalNotes.push(note);
          this.newNoteContent = '';
          this.v$.$reset();
          this.validated = false;
          this.textareaVisible = false;
        })
        .catch(() => {
          this.addError('Une erreur est survenue lors de l’ajout de la note.');
          this.$refs.sendButton.init();
        });
    },
    deleteInternalNote(note) {
      threadService
        .deleteInternalNote(note.id)
        .then(() => {
          const index = this.internalNotes.indexOf(note);
          if (index !== -1) this.internalNotes.splice(index, 1);
        })
        .catch(() => this.addError('Une erreur est survenue lors de la suppression de la note.'));
    },
    showTextArea() {
      this.textareaVisible = true;
      this.newNoteContent = '';
      this.$nextTick(() => {
        this.$refs.textarea.focus();
      });
    },
  },
};
</script>

<template>
  <div class="w-full flex p-4 items-center gap-4 h-[80px]">
    <router-link
      :to="{name: 'threads'}"
      class="text-grey-900 bg-grey-50 rounded-full flex md:hidden items-center justify-center min-h-9 min-w-9 active:bg-grey-100">
      <i class="ga-icon ga-chevron_left text-md font-bold" />
    </router-link>

    <div class="flex shrink-0">
      <div v-for="user in previewUsers" class="last:mr-0 -mr-4">
        <img class="h-[44px] w-[44px] rounded-full bg-grey-50" v-if="user.pictureUrl" :src="user.pictureUrl" />
        <span
          class="h-[44px] w-[44px] rounded-full bg-grey-50 flex items-center justify-center text-grey-500 font-semibold text-[19px]"
          v-else>
          {{ user.display }}
        </span>
      </div>
    </div>

    <span class="text-grey-900 font-bold text-lg grow truncate md:overflow-hidden md:line-clamp-2">
      {{ clientUsersConcat }}
    </span>

    <div class="flex gap-2 items-center shrink-0">
      <button
        @click="openInternalNotes"
        class="bg-grey-50 text-primary !px-3 !h-10 gap-1.5 hover:bg-grey-100 font-medium !text-[13px] focus-visible:bg-grey-100 rounded-md flex items-center">
        <template v-if="!$isMobile()">Note interne</template>
        <i v-else class="ga-icon ga-document-written text-3xl text-grey-900" />
        <span
          v-if="internalNotes.length"
          class="min-w-5 min-h-5 h-fit px-1 rounded-full text-[11px] font-semibold flex items-center justify-center"
          :class="hasUnreadInternalNotes ? 'bg-secondary text-white' : 'bg-grey-200 text-grey-600'">
          {{ internalNotes.length }}
        </span>
      </button>
      <button
        v-if="archivable"
        @click="$emit('archive')"
        v-tooltip.bottom="'Archiver'"
        class="flex items-center justify-center h-10 w-10 rounded-full bg-grey-50 cursor-pointer hover:bg-grey-100 focus-visible:bg-grey-100">
        <i class="ga-icon ga-archive text-lg text-primary" />
      </button>
    </div>
  </div>

  <AkDialogHeadless
    dismissable
    ref="internalNotesDialog"
    :show-cancel-button="false"
    :show-validate-button="false"
    contentClass="!p-3 !rounded-3xl !pb-8"
    no-header>
    <div class="flex flex-col">
      <button @click="$refs.internalNotesDialog.cancel()" class="flex w-10 h-10 items-center justify-center self-end">
        <i class="ga-icon ga-supprimer text-3xl text-grey-500" />
      </button>
    </div>
    <div v-if="!textareaVisible" class="flex flex-col gap-8 px-8">
      <div class="flex flex-col gap-2">
        <span class="font-bold text-3xl text-grey-900">Note interne</span>
        <span class="text-grey-900 font-medium block">La note interne est visible uniquement par l'équipe</span>
      </div>
      <div class="flex flex-col gap-2" v-if="internalNotes.length">
        <InternalNote
          v-for="note in internalNotes"
          :key="note.id"
          :note="note"
          :last-internal-notes-read="conversation.lastInternalNotesRead"
          @delete="deleteInternalNote" />
      </div>
      <div class="flex items-center justify-center py-8" v-else>
        <span class="text-grey-500 font-medium text-sm">Aucune note interne pour le moment</span>
      </div>
      <AkButtonActionV2
        label="Ajouter une note interne"
        @click="showTextArea"
        class="!bg-grey-50 !h-12 gap-2 font-medium text-primary !cursor-pointer">
        <template #iconLeft>
          <i class="ga-icon ga-plus text-3xl" />
        </template>
      </AkButtonActionV2>
    </div>
    <div v-else class="flex flex-col gap-6 px-8">
      <span class="text-3xl font-bold text-grey-900">Votre note</span>
      <AkFormSubmitted :submitted="validated" class="flex flex-col gap-4">
        <AkInputTextArea
          ref="textarea"
          show-character-count
          :display-label="false"
          label=""
          v-model="newNoteContent"
          :validator="v$.newNoteContent"
          placeholder="Votre note"
          class="h-[151px] [&_textarea]:resize-none" />
        <div class="flex justify-between">
          <AkButtonValidationV2
            @click="textareaVisible = false"
            label="Annuler"
            class="self-end w-fit font-medium !px-6 !h-10 btn-grey" />
          <AkButtonValidationV2
            @click="sendInternalNote"
            ref="sendButton"
            label="Ajouter"
            class="self-end w-fit font-medium !px-6 !h-10" />
        </div>
      </AkFormSubmitted>
    </div>
  </AkDialogHeadless>
</template>

<style scoped></style>
